import { FormikConfig, FormikHelpers, getIn, useFormik } from 'formik'
import { Generator, GeneratorInput, LoadAutomationMode, PowerSource } from '../types'
import * as Yup from 'yup'
import {
  isRequired,
  mustBeGreaterThan,
  mustBeGreaterThanOrEqualTo,
  mustBeLessThanOrEqualTo,
  mustBeNumber,
  mustBePositive,
  translateValidationErrors
} from '../utils/validation'
import { useTranslation } from 'react-i18next'
import { createGenerator, updateGenerator } from '../services/generator'
import { useEffect, useMemo } from 'react'
import { calculateNominalCurrent } from '../utils/generator'
import { CatalogProductStatus, CatalogProductType, PowerUnit } from '../enums'
import { useSettings } from '../contexts/SettingsContext'
import { convertAmperesToKilovoltAmperes, convertKilowattsToAmperes } from '../utils/convert'
import { ObjectShape } from 'yup/lib/object'
import axios from 'axios'

interface GeneratorFormProps extends Omit<
  FormikConfig<GeneratorInput>,
  'initialValues' | 'onSubmit'
> {
  generator?: Generator
  field?: string
  initialValues?: FormikConfig<GeneratorInput>['initialValues']
  onSubmit?: FormikConfig<GeneratorInput>['onSubmit']
  afterSubmit?: (generator: Generator) => void
  afterError?: (error: unknown) => void
}

export function useGeneratorForm({
  generator,
  field,
  afterSubmit,
  afterError,
  initialValues,
  validationSchema,
  onSubmit,
  ...props
}: GeneratorFormProps) {
  const { t } = useTranslation('generator')

  const { settings } = useSettings()

  const defaultInitialValues: GeneratorInput = {
    apparentPower: generator?.apparentPower || 0,
    nominalVoltage: generator?.nominalVoltage || 230,
    nominalCurrent: generator?.nominalCurrent || null,
    powerSource: generator?.powerSource || PowerSource.DIESEL,
    serialNumber: generator?.serialNumber || null,
    customerReference: generator?.customerReference || null,
    additionalLoad: generator?.additionalLoad,
    additionalLoadUnit: generator?.additionalLoadUnit,
    relayOnThreshold: generator?.relayOnThreshold,
    relayOffThreshold: generator?.relayOffThreshold,
    relayOffDelay: generator?.relayOffDelay,
    relayOnDelay: generator?.relayOnDelay,
    loadAutomationMode: generator?.loadAutomationMode,
    catalogProduct: {
      id: generator?.catalogProduct?.id,
      type: CatalogProductType.GENERATOR,
      name: generator?.catalogProduct?.name || '',
      manufacturer: generator?.catalogProduct?.manufacturer || '',
      manufacturerProductCode: generator?.catalogProduct?.manufacturerProductCode,
      status: generator?.catalogProduct?.status || CatalogProductStatus.UNVERIFIED,
      customData: {
        apparentPower: generator?.catalogProduct?.customData?.apparentPower,
        nominalVoltage: generator?.catalogProduct?.customData?.nominalVoltage,
        nominalCurrent: generator?.catalogProduct?.customData?.nominalCurrent,
        powerSource: generator?.catalogProduct?.customData?.powerSource
      }
    }
  }

  const calculatedNominalCurrent = useMemo(() => {
    if (!generator) {
      return null
    }

    const rounded = Math.round(calculateNominalCurrent(generator) * 100) / 100

    return rounded
  }, [generator?.apparentPower, generator?.nominalVoltage])

  const maxNominalCurrent = calculatedNominalCurrent ?? 1200

  const defaultValidationSchema = makeValidationSchema()

  function makeValidationSchema() {
    const schema: ObjectShape = {}

    if (field === 'apparentPower') {
      schema['apparentPower'] = Yup.number()
        .typeError(mustBeNumber(t('apparentPower')))
        .min(0, mustBeGreaterThan(t('apparentPower'), 0))
        .required(isRequired(t('apparentPower')))
    }

    if (field === 'nominalVoltage') {
      schema['nominalVoltage'] = Yup.number()
        .typeError(mustBeNumber(t('nominalVoltage')))
        .required(isRequired(t('nominalVoltage')))
    }

    if (field === 'nominalCurrent') {
      schema['nominalCurrent'] = Yup.number()
        .typeError(mustBeNumber(t('nominalCurrent')))
        .min(2.5, mustBeGreaterThanOrEqualTo(t('nominalCurrent'), 2.5))
        .max(maxNominalCurrent, mustBeLessThanOrEqualTo(t('nominalCurrent'), maxNominalCurrent))
        .nullable()
    }

    if (field === 'powerSource') {
      schema['powerSource'] = Yup.mixed<PowerSource>()
        .oneOf(Object.values(PowerSource))
        .required(isRequired(t('powerSource')))
    }

    if (
      field === 'additionalLoad' ||
      field === 'relayOnThreshold' ||
      field === 'relayOffThreshold' ||
      field === 'relayOnDelay' ||
      field === 'relayOffDelay' ||
      field === 'loadAutomationMode'
    ) {
      schema['loadAutomationMode'] = Yup.mixed<LoadAutomationMode>().oneOf(
        Object.values(LoadAutomationMode)
      )
      schema['additionalLoad'] = Yup.number()
        .typeError(mustBeNumber(t('additionalLoad')))
        .min(0, mustBeGreaterThanOrEqualTo(t('additionalLoad'), 0))
        .nullable()
        .optional()
      schema['relayOnThreshold'] = Yup.number()
        .typeError(mustBeNumber(t('threshold')))
        .min(0, mustBeGreaterThanOrEqualTo(t('threshold'), 0))
        .max(100, mustBeLessThanOrEqualTo(t('threshold'), 100))
        .nullable()
        .optional()
      schema['relayOffThreshold'] = Yup.number()
        .typeError(mustBeNumber(t('threshold')))
        .min(0, mustBeGreaterThanOrEqualTo(t('threshold'), 0))
        .max(100, mustBeLessThanOrEqualTo(t('threshold'), 100))
        .nullable()
        .optional()
      schema['relayOnDelay'] = Yup.number()
        .typeError(mustBeNumber(t('relayOnDelay')))
        .min(0, mustBePositive(t('relayOnDelay')))
        .optional()
      schema['relayOffDelay'] = Yup.number()
        .typeError(mustBeNumber(t('relayOffDelay')))
        .min(0, mustBePositive(t('relayOffDelay')))
        .optional()
    }

    return Yup.object(schema)
  }

  const { values, errors, touched, setFieldValue, setFieldError, ...formik } = useFormik({
    initialValues: initialValues ?? defaultInitialValues,
    validationSchema: validationSchema ?? defaultValidationSchema,
    enableReinitialize: true,
    onSubmit: onSubmit ?? handleSubmit,
    ...props
  })

  useEffect(() => {
    if (
      generator?.catalogProduct?.status === CatalogProductStatus.UNVERIFIED &&
      generator?.catalogProduct?.customData?.apparentPower &&
      generator.catalogProduct.customData.apparentPower !== values.apparentPower
    ) {
      setFieldValue('catalogProduct.apparentPower', values.apparentPower)
    }
  }, [generator?.catalogProduct, values.apparentPower])

  useEffect(() => {
    if (
      generator?.catalogProduct?.status === CatalogProductStatus.UNVERIFIED &&
      generator?.catalogProduct?.customData?.nominalVoltage &&
      generator.catalogProduct.customData.nominalVoltage !== values.nominalVoltage
    ) {
      setFieldValue('catalogProduct.nominalVoltage', values.nominalVoltage)
    }
  }, [generator?.catalogProduct, values.nominalVoltage])

  useEffect(() => {
    if (
      generator?.catalogProduct?.status === CatalogProductStatus.UNVERIFIED &&
      generator?.catalogProduct?.customData?.nominalCurrent &&
      generator.catalogProduct.customData.nominalCurrent !== values.nominalCurrent
    ) {
      setFieldValue('catalogProduct.nominalCurrent', values.nominalCurrent)
    }
  }, [generator?.catalogProduct, values.nominalCurrent])

  useEffect(() => {
    if (
      generator?.catalogProduct?.status === CatalogProductStatus.UNVERIFIED &&
      generator?.catalogProduct?.customData?.powerSource &&
      generator.catalogProduct.customData.powerSource !== values.powerSource
    ) {
      setFieldValue('catalogProduct.powerSource', values.powerSource)
    }
  }, [generator?.catalogProduct, values.powerSource])

  function isError(field: string) {
    const fieldError = getIn(errors, field)
    const fieldTouched = getIn(touched, field)

    return Boolean(fieldError && fieldTouched)
  }

  function getHelperText(field: string) {
    const fieldError = getIn(errors, field)
    const fieldTouched = getIn(touched, field)

    return fieldTouched && fieldError ? fieldError : undefined
  }

  async function handleSubmit(
    values: GeneratorInput,
    { setSubmitting }: FormikHelpers<GeneratorInput>
  ) {
    try {
      setSubmitting(true)

      if (values.nominalCurrent !== null) {
        // Calculate the apparent power.
        values.apparentPower = convertAmperesToKilovoltAmperes(
          values.nominalCurrent,
          values.nominalVoltage
        )
      }

      if (
        settings.generator.additionalLoadUnit === PowerUnit.AMPERE &&
        values.additionalLoad !== undefined
      ) {
        values.additionalLoad = convertKilowattsToAmperes(
          values.additionalLoad,
          values.nominalVoltage,
          1
        )
      }

      if (generator) {
        const updatedGenerator = await updateGenerator(generator.id, values)
        afterSubmit?.(updatedGenerator)
      } else {
        const createdGenerator = await createGenerator(values)
        afterSubmit?.(createdGenerator)
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 422 &&
        typeof error.response.data?.validation === 'object'
      ) {
        const errors = translateValidationErrors(error.response.data.validation)
        console.log(errors)
        Object.keys(errors).forEach((field) => {
          if (
            Array.isArray(errors[field]) &&
            errors[field].length > 0 &&
            typeof errors[field][0] === 'string'
          ) {
            setFieldError(field, errors[field][0])
          } else if (typeof errors[field] === 'string') {
            setFieldError(field, errors[field])
          }
        })
      }

      afterError?.(error)
    } finally {
      setSubmitting(false)
    }
  }

  return {
    values,
    touched,
    errors,
    setFieldValue,
    ...formik,
    isError,
    getHelperText
  }
}
